<template>
  <div v-bind="$attrs">
    <label>
      {{ label }}
      <v-text-field
        v-model="value"
        v-bind="getProps"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :autofocus="autofocus"
        :error="!!error"
        :error-messages="error ? [error] : undefined"
        :bg-color="readonly ? 'background' : 'surface'"
        :validate-on="isValidInternal || isPristineInternal ? 'blur' : 'input'"
        :rules="rules"
        :suffix="suffix"
        :prefix="prefix"
        :name="name"
        :hide-details="hideDetails"
        :hint="hint"
        :persistent-hint="!!hint"
        :clearable="clearable"
        :append-inner-icon="appendIcon"
        :min="min"
        :max="max"
        :maxlength="maxlength"
        data-testid="so-text-field"
        color="success"
        variant="solo"
        density="comfortable"
        class="mb-1"
        @blur="$emit('blur')"
      >
        <template #append="{ isValid, isPristine }">
          {{ assignToData(isValid.value, isPristine.value) }}
          <slot name="append" />
        </template>
      </v-text-field>
    </label>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { ValidationRule } from "@/types/ValidationRule"

  type SoTextFieldTypeProps = {
    type: string
  }

  interface SoTextFieldTypes {
    readonly [char: string]: SoTextFieldTypeProps
  }

  const TYPES: SoTextFieldTypes = {
    text: {
      type: "text",
    },
    number: {
      type: "number",
    },
    password: {
      type: "password",
    },
  }

  export default defineComponent({
    inheritAttrs: false,

    props: {
      modelValue: {
        type: [String, Number] as PropType<string | number | null>,
        default: undefined,
      },
      label: {
        type: String,
        default: undefined,
      },
      type: {
        type: String,
        default: "text",
        validator: (val: string) => val in TYPES,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      autofocus: {
        type: Boolean,
        default: false,
      },
      error: {
        type: String,
        default: undefined,
      },
      hideDetails: {
        type: [Boolean, String] as PropType<boolean | "auto">,
        default: "auto",
      },
      placeholder: {
        type: String,
        default: undefined,
      },
      hint: {
        type: String,
        default: undefined,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: [] as ValidationRule[],
      },
      suffix: {
        type: String,
        default: undefined,
      },
      prefix: {
        type: String,
        default: undefined,
      },
      name: {
        type: String,
        default: undefined,
      },
      min: {
        type: Number,
        default: undefined,
      },
      max: {
        type: Number,
        default: undefined,
      },
      maxlength: {
        type: Number,
        default: undefined,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      appendIcon: {
        type: String,
        default: undefined,
      },
    },
    emits: ["update:modelValue", "blur"],
    data() {
      return {
        isValidInternal: null as null | boolean,
        isPristineInternal: null as null | boolean,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(val: string) {
          this.$emit("update:modelValue", val)
        },
      },
      getProps() {
        return TYPES[this.type]
      },
    },
    methods: {
      assignToData(isValid: boolean | null, isPristine: boolean | null) {
        this.isValidInternal = isValid
        this.isPristineInternal = isPristine
      },
    },
  })
</script>

<style scoped>
  :deep([data-lastpass-icon-root]) {
    display: none;
  }
  :deep(.v-input__append) {
    margin-inline-start: 0;
  }
  :deep(.v-field) {
    height: 48px !important;
    border-radius: var(--theme-radius);
    transition: border 250ms;
  }
  :deep(.v-field__input) {
    top: -2px;
  }
  :deep(.v-field:hover) {
    border: 1px solid rgb(var(--v-theme-on-surface));
  }
  :deep(.v-field--focused) {
    outline: var(--theme-outline);
  }
  :deep(.v-field--error) {
    border: 1px solid rgb(var(--v-theme-error)) !important;
  }
  :deep(.v-field--variant-solo) {
    box-shadow: none !important;
    border: 1px solid rgb(var(--v-theme-border));
  }
  :deep(.v-messages__message) {
    line-height: normal;
  }
</style>
